/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * Per-view unread push-notification counters for the CURRENT sales account,
 * surfaced to the menus (side menu + bottom nav) as badges.
 *
 * Flow:
 *  - The service worker increments a per-(account, route) counter in IndexedDB
 *    on every push (works while the app is closed) and posts COUNTERS_CHANGED.
 *  - This provider loads the current account's counts, updates live on that SW
 *    message / a cross-tab BroadcastChannel / tab focus, and resets a route's
 *    counter the moment the user opens that view (one central place, so the
 *    individual view controllers stay untouched).
 *  - It also keeps the installed-PWA app-icon badge in sync with the total
 *    unread across everything.
 */

import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';

import {useLocation} from 'react-router';

import {AppContext} from './appContext';

import {NOTIFICATIONS_ROUTE, ORDERS_ROUTE, REVIEW_ANALYTICS_ROUTE} from '../../util/constants';

import {getTotalUnreadCount, getUnreadCountsForAccount, resetUnreadCount} from '../../util/pushIdb';

// Routes that can carry an unread badge. MUST match the pathnames the backend
// sets as a push target URL (see get_notification_click_url in
// notification_helper): Reviews -> /review-analytics; every other type (Order,
// Buy Box, Stock, Product hijack) -> /facturen/orders, so the Orders item badges
// those. NOTIFICATIONS_ROUTE is kept for any legacy push still targeting it.
const BADGED_ROUTES = [NOTIFICATIONS_ROUTE, REVIEW_ANALYTICS_ROUTE, ORDERS_ROUTE];

const BROADCAST_CHANNEL = 'bluemate-unread-counters';

type UnreadCounts = Record<string, number>;

const NotificationBadgeContext = createContext<UnreadCounts>({});

/** Per-route unread counts for the current account, e.g. {'/notificaties': 3}. */
export function useNotificationBadges(): UnreadCounts {
    return useContext(NotificationBadgeContext);
}

/** Keep the installed-PWA app-icon badge in sync with the total unread. */
async function syncAppBadge(): Promise<void> {
    try {
        if (typeof navigator === 'undefined') return;
        const nav = navigator as unknown as {
            setAppBadge?: (n: number) => Promise<void>;
            clearAppBadge?: () => Promise<void>;
        };
        if (!nav.setAppBadge) return;
        const total = await getTotalUnreadCount();
        if (total > 0) await nav.setAppBadge(total);
        else await nav.clearAppBadge?.();
    } catch (_e) {
        /* best-effort */
    }
}

export function NotificationBadgeProvider({children}: {children: React.ReactNode}) {
    const {account} = useContext(AppContext).accountContext;
    const accountId = account?.id ?? null;
    const location = useLocation();
    const [counts, setCounts] = useState<UnreadCounts>({});

    const reload = useCallback(async () => {
        if (!accountId) {
            setCounts({});
            void syncAppBadge();
            return;
        }
        setCounts(await getUnreadCountsForAccount(accountId));
        void syncAppBadge();
    }, [accountId]);

    // Initial load + live updates: SW push message, cross-tab broadcast, focus.
    useEffect(() => {
        void reload();

        function onSwMessage(event: MessageEvent) {
            if (event.data?.type === 'COUNTERS_CHANGED') void reload();
        }
        navigator.serviceWorker?.addEventListener('message', onSwMessage);

        let channel: BroadcastChannel | null = null;
        if (typeof BroadcastChannel !== 'undefined') {
            try {
                channel = new BroadcastChannel(BROADCAST_CHANNEL);
                channel.onmessage = () => void reload();
            } catch (_e) {
                channel = null;
            }
        }

        function onVisible() {
            if (document.visibilityState === 'visible') void reload();
        }
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            navigator.serviceWorker?.removeEventListener('message', onSwMessage);
            channel?.close();
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [reload]);

    // Reset-on-visit: when the current path matches a badged route that still
    // has unread items, clear it. Also auto-clears a push that arrives while the
    // user is already on the view (counts change → effect re-runs → reset).
    useEffect(() => {
        if (!accountId) return;
        const path = location.pathname;
        const matched = BADGED_ROUTES.find(route => path === route || path.startsWith(`${route}/`));
        if (!matched || !counts[matched]) return;
        void (async () => {
            await resetUnreadCount(accountId, matched);
            setCounts(prev => {
                if (!prev[matched]) return prev;
                const next = {...prev};
                delete next[matched];
                return next;
            });
            void syncAppBadge();
            if (typeof BroadcastChannel !== 'undefined') {
                try {
                    const channel = new BroadcastChannel(BROADCAST_CHANNEL);
                    channel.postMessage({type: 'RESET'});
                    channel.close();
                } catch (_e) {
                    /* best-effort */
                }
            }
        })();
    }, [location.pathname, accountId, counts]);

    return <NotificationBadgeContext.Provider value={counts}>{children}</NotificationBadgeContext.Provider>;
}
